import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
    TbManualGearbox, // Schimbător viteze
    TbWheel,         // Roată / Jantă
    TbEngine,        // Motor
    TbGasStation,    // Pompă combustibil
    TbCalendarTime,  // An / Timp
    TbRoad           // Kilometraj / Drum
} from "react-icons/tb";
import { API_URL, UPLOADS_URL, generateSlug } from './config';
import { apiGet } from './api';
import { formatNumar, formatPret } from './format';
import useIsMobile from './useIsMobile';
import './App.css';

const BROKEN_IMG_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' fill='none'%3E%3Crect width='800' height='600' rx='0' fill='%23f3f4f6'/%3E%3Cpath d='M370 280h60v8h-60z' fill='%23d1d5db'/%3E%3Ccircle cx='400' cy='260' r='24' stroke='%23d1d5db' stroke-width='4' fill='none'/%3E%3Cpath d='M388 248l24 24M412 248l-24 24' stroke='%23d1d5db' stroke-width='3' stroke-linecap='round'/%3E%3Ctext x='50%25' y='340' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16' fill='%239ca3af'%3EImaginea nu a putut fi %C3%AEnc%C4%83rcat%C4%83%3C/text%3E%3C/svg%3E";

function MasinaDetalii() {
    const { slug } = useParams();
    const location = useLocation();
    const [masina, setMasina] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [cursValutar, setCursValutar] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const loadedImagesRef = useRef(new Set());
    const [copied, setCopied] = useState(false);
    const isMobile = useIsMobile();

    // Carusel: track-ul urmareste degetul (dragX in px), apoi face snap animat
    const [dragX, setDragX] = useState(0);
    const [animating, setAnimating] = useState(null); // 'next' | 'prev' | 'cancel'
    const galleryRef = useRef(null);
    const galleryDragRef = useRef({ active: false });
    const thumbStripRef = useRef(null);

    // Lightbox: zoom (dublu-tap), pan cand e zoom, tragere in jos = inchidere
    const [lbZoom, setLbZoom] = useState(1);
    const [lbPan, setLbPan] = useState({ x: 0, y: 0 });
    const [lbDragY, setLbDragY] = useState(0);
    const [lbTouching, setLbTouching] = useState(false);
    const lbTouchRef = useRef({});
    const lbLastTapRef = useRef(0);

    // Culorile tale de accent
    const primaryOrange = "#ff8e18";
    const trustNavy = "#1c2e4a";

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = 'Detalii Vehicul | SPCheapCars';

        const carId = location.state?.carId;

        if (carId) {
            // In-app navigation — fetch directly by ID
            apiGet(`/parc-auto/${carId}`)
                .then(data => { setMasina(data); document.title = `${data.marca} ${data.model} ${data.an} | SPCheapCars`; })
                .catch(() => setNotFound(true));
        } else {
            // Direct URL / shared link — resolve slug
            apiGet('/parc-auto')
                .then(cars => {
                    const match = cars.find(m => generateSlug(m) === slug);
                    if (!match) { setNotFound(true); return null; }
                    return apiGet(`/parc-auto/${match.id}`);
                })
                .then(data => {
                    if (!data) return;
                    setMasina(data);
                    document.title = `${data.marca} ${data.model} ${data.an} | SPCheapCars`;
                })
                .catch(() => setNotFound(true));
        }

        // acelasi curs ca in restul aplicatiei, servit de backend
        apiGet('/curs')
            .then(data => setCursValutar(data.eurRon))
            .catch(() => {});
    }, [slug]);

    const imaginiValide = masina && masina.imagini ? masina.imagini.filter(img => img.numeFisier) : [];
    const galerieFoto = imaginiValide.length > 0
        ? imaginiValide.map(img => `${UPLOADS_URL}/${img.numeFisier}`)
        : [BROKEN_IMG_PLACEHOLDER];
    // strip-ul de sub imagine foloseste miniaturile (mult mai mici la transfer)
    const galerieThumb = imaginiValide.length > 0
        ? imaginiValide.map(img => `${UPLOADS_URL}/${img.thumbnail || img.numeFisier}`)
        : [BROKEN_IMG_PLACEHOLDER];

    // Incarcare progresiva: aratam intai miniatura (deja in cache din strip),
    // iar originalul se descarca in fundal si inlocuieste miniatura cand e gata.
    const [loadedOriginals, setLoadedOriginals] = useState(() => new Set());

    useEffect(() => {
        if (galerieFoto.length === 0) return;
        const preload = (idx) => {
            const url = galerieFoto[idx];
            if (!url || url === BROKEN_IMG_PLACEHOLDER || loadedOriginals.has(url)) return;
            const img = new Image();
            img.onload = () => {
                loadedImagesRef.current.add(url);
                setLoadedOriginals(prev => {
                    if (prev.has(url)) return prev;
                    const next = new Set(prev);
                    next.add(url);
                    return next;
                });
            };
            img.src = url;
        };
        preload(currentImageIndex);
        // preincarcam vecinii ca swipe-ul sa fie instant
        if (galerieFoto.length > 1) {
            preload((currentImageIndex + 1) % galerieFoto.length);
            preload((currentImageIndex - 1 + galerieFoto.length) % galerieFoto.length);
        }
    }, [currentImageIndex, masina]);

    // originalul daca e descarcat, altfel miniatura (progresiv)
    const srcFor = (idx) => (loadedOriginals.has(galerieFoto[idx]) ? galerieFoto[idx] : galerieThumb[idx]);
    const mainImageSrc = srcFor(currentImageIndex);

    const handlePrevImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === 0 ? galerieFoto.length - 1 : prev - 1));
    }, [galerieFoto.length]);

    const handleNextImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === galerieFoto.length - 1 ? 0 : prev + 1));
    }, [galerieFoto.length]);

    useEffect(() => {
        if (!mainImageSrc) return;
        if (!loadedImagesRef.current.has(mainImageSrc)) {
            setImageLoading(true);
        }
    }, [mainImageSrc]);

    const closeLightbox = useCallback(() => {
        setShowLightbox(false);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (showLightbox) {
                if (e.key === 'ArrowLeft') handlePrevImage();
                if (e.key === 'ArrowRight') handleNextImage();
                if (e.key === 'Escape') closeLightbox();
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [showLightbox, handlePrevImage, handleNextImage, closeLightbox]);

    // Blocheaza scroll-ul paginii cat timp lightbox-ul e deschis (iOS-safe)
    useEffect(() => {
        if (!showLightbox) return;
        const scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            const y = Math.abs(parseInt(document.body.style.top || '0', 10));
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, y);
        };
    }, [showLightbox]);

    // Reset zoom/pan la schimbarea imaginii sau la deschidere/inchidere
    useEffect(() => {
        setLbZoom(1);
        setLbPan({ x: 0, y: 0 });
        setLbDragY(0);
    }, [currentImageIndex, showLightbox]);

    // Tine miniatura activa vizibila in strip
    useEffect(() => {
        const strip = thumbStripRef.current;
        const el = strip && strip.children[currentImageIndex];
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        }
    }, [currentImageIndex]);

    if (notFound) return (
        <div className="d-flex justify-content-center align-items-center" style={{minHeight: '60vh', paddingTop: isMobile ? 0 : '120px'}}>
            <div className="text-center">
                <h1 style={{fontSize: '4rem', fontWeight: '800', color: trustNavy}}>404</h1>
                <p style={{color: '#6b7280', marginBottom: '24px'}}>Mașina nu a fost găsită</p>
                <Link to="/" style={{color: primaryOrange, fontWeight: '600', textDecoration: 'none'}}>Înapoi la Showroom</Link>
            </div>
        </div>
    );

    if (!masina) return (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{minHeight: '60vh'}}>
            <div className="spinner-border" style={{color: primaryOrange}} role="status">
                <span className="visually-hidden">Se încarcă...</span>
            </div>
            <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '0.875rem' }}>Se încarcă detaliile vehiculului...</p>
        </div>
    );

    const listaDotari = masina.dotari
        ? masina.dotari.split(/[,;\n]+/).filter(d => d.trim().length > 0)
        : [];

    const handleThumbnailClick = (index) => {
        setCurrentImageIndex(index);
    };

    const openLightbox = (index) => {
        setCurrentImageIndex(index);
        setShowLightbox(true);
    };

    const handleShare = async () => {
        const shareData = {
            title: `${masina.marca} ${masina.model} ${masina.an} | SPCheapCars`,
            url: window.location.href
        };
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // AbortError = utilizatorul a inchis meniul de share; nu e o eroare
                if (err.name !== 'AbortError') throw err;
            }
        } else {
            navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // ==================== CARUSEL (slide sub deget) ====================
    const multi = galerieFoto.length > 1;
    const prevIdx = (currentImageIndex - 1 + galerieFoto.length) % galerieFoto.length;
    const nextIdx = (currentImageIndex + 1) % galerieFoto.length;
    // 3 slide-uri [prev, curent, next]; pozitia de repaus e -100% (slide-ul din mijloc)
    const slideIndices = multi ? [prevIdx, currentImageIndex, nextIdx] : [currentImageIndex];
    const trackBase = multi ? -100 : 0;
    const trackTransform = animating === 'next'
        ? 'translateX(-200%)'
        : animating === 'prev'
            ? 'translateX(0%)'
            : animating === 'cancel'
                ? 'translateX(-100%)'
                : `translateX(calc(${trackBase}% + ${dragX}px))`;
    const trackTransition = animating ? 'transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)' : 'none';

    const slideTo = (dir) => {
        if (!multi || animating) return;
        setAnimating(dir);
    };

    const onTrackTransitionEnd = (e) => {
        if (e.target !== e.currentTarget) return; // ignora tranzitiile copiilor (ex. opacity pe img)
        if (animating === 'next') setCurrentImageIndex(nextIdx);
        if (animating === 'prev') setCurrentImageIndex(prevIdx);
        setAnimating(null);
        setDragX(0);
    };

    const onGalleryTouchStart = (e) => {
        if (!multi || animating) return;
        const t = e.targetTouches[0];
        galleryDragRef.current = {
            active: true,
            startX: t.clientX,
            startY: t.clientY,
            horizontal: null,
            width: galleryRef.current ? galleryRef.current.offsetWidth : window.innerWidth,
            startT: Date.now()
        };
    };

    const onGalleryTouchMove = (e) => {
        const d = galleryDragRef.current;
        if (!d.active) return;
        const t = e.targetTouches[0];
        const dx = t.clientX - d.startX;
        const dy = t.clientY - d.startY;
        if (d.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
            d.horizontal = Math.abs(dx) > Math.abs(dy);
        }
        if (d.horizontal) setDragX(dx);
    };

    const onGalleryTouchEnd = () => {
        const d = galleryDragRef.current;
        if (!d.active) return;
        d.active = false;
        if (!d.horizontal) { setDragX(0); return; }
        const dt = Math.max(Date.now() - d.startT, 1);
        const speed = Math.abs(dragX) / dt; // px/ms — flick scurt = schimbare
        const past = Math.abs(dragX) > d.width * 0.2 || speed > 0.5;
        if (past && dragX < 0) setAnimating('next');
        else if (past && dragX > 0) setAnimating('prev');
        else if (dragX !== 0) setAnimating('cancel');
    };

    // ==================== LIGHTBOX (gesturi) ====================
    const toggleLbZoom = () => {
        if (lbZoom > 1) {
            setLbZoom(1);
            setLbPan({ x: 0, y: 0 });
        } else {
            setLbZoom(2.2);
        }
    };

    const onLbTouchStart = (e) => {
        const t = e.targetTouches[0];
        lbTouchRef.current = {
            startX: t.clientX,
            startY: t.clientY,
            panStart: { ...lbPan },
            moved: false,
            dir: null
        };
        setLbTouching(true);
    };

    const onLbTouchMove = (e) => {
        const r = lbTouchRef.current;
        if (r.startX === undefined) return;
        const t = e.targetTouches[0];
        const dx = t.clientX - r.startX;
        const dy = t.clientY - r.startY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) r.moved = true;
        if (lbZoom > 1) {
            setLbPan({ x: r.panStart.x + dx, y: r.panStart.y + dy });
            return;
        }
        if (!r.dir && r.moved) r.dir = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (r.dir === 'v' && dy > 0) setLbDragY(dy);
    };

    const onLbTouchEnd = (e) => {
        const r = lbTouchRef.current;
        setLbTouching(false);
        if (!r.moved) {
            // dublu-tap = zoom in/out
            const now = Date.now();
            if (now - lbLastTapRef.current < 300) {
                toggleLbZoom();
                lbLastTapRef.current = 0;
            } else {
                lbLastTapRef.current = now;
            }
        } else if (lbZoom === 1 && e.changedTouches.length > 0) {
            const t = e.changedTouches[0];
            const dx = t.clientX - r.startX;
            const dy = t.clientY - r.startY;
            if (r.dir === 'v' && dy > 120) {
                closeLightbox();
            } else if (r.dir === 'h' && Math.abs(dx) > 50) {
                if (dx < 0) handleNextImage(); else handlePrevImage();
            }
        }
        setLbDragY(0);
    };

    // ==================== STILURI COMUNE ====================
    const cardStyles = {
        container: {
            background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.06)',
            marginBottom: '20px'
        },
        header: isMobile ? {
            padding: '14px 16px 12px',
            borderLeft: `4px solid ${primaryOrange}`,
            marginLeft: '16px',
            marginRight: '16px',
            marginTop: '16px',
            background: 'transparent'
        } : {
            background: `linear-gradient(135deg, ${trustNavy} 0%, #2a4266 100%)`,
            padding: '16px 24px',
            position: 'relative',
            overflow: 'hidden'
        },
        headerPattern: {
            position: 'absolute',
            top: 0,
            right: 0,
            width: '120px',
            height: '120px',
            background: `radial-gradient(circle at 100% 0%, ${primaryOrange}15 0%, transparent 70%)`,
            pointerEvents: 'none'
        },
        headerTitle: isMobile ? {
            color: trustNavy,
            fontSize: '1rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        } : {
            color: '#ffffff',
            fontSize: '0.75rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        body: {
            padding: isMobile ? '12px 16px 16px' : '24px'
        }
    };

    // Stiluri pentru Specificații
    const specStyles = {
        row: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f0f0f0'
        },
        rowLast: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0'
        },
        label: {
            color: '#6c757d',
            fontSize: '0.875rem',
            fontWeight: '400'
        },
        value: {
            color: trustNavy,
            fontSize: '0.875rem',
            fontWeight: '600'
        },
        valueHighlight: {
            color: primaryOrange,
            fontSize: '0.875rem',
            fontWeight: '700'
        }
    };

    // Stiluri pentru Dotări
    const dotariStyles = {
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '0'
        },
        item: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            borderBottom: '1px solid #f5f5f5',
            borderRight: '1px solid #f5f5f5',
            transition: 'background 0.2s ease'
        },
        dot: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${primaryOrange} 0%, #e67e00 100%)`,
            flexShrink: 0
        },
        text: {
            color: trustNavy,
            fontSize: '0.875rem',
            fontWeight: '500',
            textTransform: 'capitalize'
        }
    };

    // Stiluri pentru sidebar
    const sidebarStyles = {
        container: {
            background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.06)'
        },
        priceHeader: {
            background: `linear-gradient(135deg, ${trustNavy} 0%, #2a4266 100%)`,
            padding: '24px',
            position: 'relative',
            overflow: 'hidden'
        },
        priceHeaderPattern: {
            position: 'absolute',
            top: 0,
            right: 0,
            width: '150px',
            height: '150px',
            background: `radial-gradient(circle at 100% 0%, ${primaryOrange}20 0%, transparent 70%)`,
            pointerEvents: 'none'
        },
        carTitle: {
            color: 'rgba(255,255,255,0.9)',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '8px',
            letterSpacing: '0.3px'
        },
        priceMain: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
        },
        priceEuro: {
            color: '#ffffff',
            fontSize: '2.5rem',
            fontWeight: '700',
            lineHeight: '1',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontFamily: 'var(--font-display)'
        },
        priceRon: {
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            padding: '6px 12px',
            borderRadius: '20px',
            color: 'rgba(255,255,255,0.9)',
            fontSize: '0.75rem',
            fontWeight: '500'
        },
        contactSection: {
            padding: '20px 24px'
        },
        btnPhone: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '14px 20px',
            background: trustNavy,
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: '600',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
            marginBottom: '10px'
        },
        btnWhatsapp: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '14px 20px',
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: '600',
            textDecoration: 'none',
            transition: 'all 0.2s ease'
        },
        btnRaport: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '14px 20px',
            // albastrul oficial carVertical (#0073F0, preluat de pe carvertical.com)
            background: 'linear-gradient(135deg, #0073F0 0%, #005AD1 100%)',
            border: 'none',
            borderRadius: '10px',
            color: '#ffffff',
            fontSize: '1rem',
            fontWeight: '600',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
            marginTop: '10px'
        },
        servicesSection: {
            padding: '0 24px 20px'
        },
        servicesHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px'
        },
        servicesLine: {
            flex: 1,
            height: '1px',
            background: 'linear-gradient(90deg, #e0e0e0 0%, transparent 100%)'
        },
        servicesTitle: {
            color: trustNavy,
            fontSize: '0.75rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        serviceItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            padding: '12px 0',
            borderBottom: '1px solid #f0f0f0'
        },
        serviceItemLast: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            padding: '12px 0'
        },
        serviceIcon: {
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${primaryOrange}15 0%, ${primaryOrange}05 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        },
        serviceTitle: {
            color: trustNavy,
            fontSize: '0.875rem',
            fontWeight: '600',
            marginBottom: '2px'
        },
        serviceDesc: {
            color: '#6c757d',
            fontSize: '0.75rem',
            margin: 0
        },
        trustBanner: {
            margin: '0 24px 20px',
            padding: '14px 16px',
            background: `linear-gradient(135deg, ${primaryOrange}08 0%, ${primaryOrange}03 100%)`,
            borderRadius: '10px',
            border: `1px solid ${primaryOrange}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        },
        trustText: {
            color: trustNavy,
            fontSize: '0.75rem',
            fontWeight: '600'
        },
        footer: {
            padding: '16px 24px',
            background: '#f8f9fa',
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center'
        },
        footerText: {
            color: '#6b7280',
            fontSize: '0.75rem',
            margin: 0,
            fontStyle: 'italic'
        }
    };

    // Date pentru specificații - organizate
    const isElectric = masina.combustibil === 'Electric';

    const specificatiiStanga = [
        { label: 'Marcă', value: masina.marca },
        { label: 'Model', value: masina.model },
        { label: 'An Fabricație', value: masina.an },
        { label: 'Rulaj', value: `${formatNumar(masina.kilometraj)} km` },
        { label: 'Combustibil', value: masina.combustibil },
        { label: 'Putere', value: masina.putere ? `${masina.putere} CP` : '—' }
    ];

    const specificatiiDreapta = [
        !isElectric && { label: 'Motorizare', value: masina.motor ? `${masina.motor} cm³` : '—' },
        { label: 'Normă Poluare', value: isElectric ? 'Zero Emisii' : (masina.normaPoluare || '—') },
        { label: 'Cutie Viteze', value: masina.cutieViteze },
        { label: 'Tracțiune', value: masina.tractiune },
        { label: 'Culoare', value: masina.culoare },
        { label: 'VIN', value: masina.vin || "Nedisponibil" }
    ].filter(Boolean);

    return (
        <div className="bg-light min-vh-100" style={{ paddingTop: isMobile ? '12px' : '120px', paddingBottom: '3rem' }}>
            <div className="container">

                {/* Breadcrumb */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <nav aria-label="breadcrumb">
                        <div
                            className="d-inline-flex align-items-center gap-2 px-4 py-2"
                            style={{
                                background: 'white',
                                borderRadius: '30px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Link
                                to="/"
                                className="text-decoration-none d-flex align-items-center gap-1"
                                style={{color: '#6c757d', fontSize: '0.875rem'}}
                            >
                                <i className="bi bi-house-door"></i>
                                Acasă
                            </Link>
                            <i className="bi bi-chevron-right" style={{fontSize: '0.7rem', color: '#ccc'}}></i>
                            <span style={{color: trustNavy, fontWeight: '600', fontSize: '0.875rem'}}>
                                {masina.marca} {masina.model}
                            </span>
                        </div>
                    </nav>
                </div>

                <div className="row g-4" style={{ alignItems: 'flex-start' }}>
                    {/* ==================== COLOANA STÂNGA - CONȚINUT PRINCIPAL ==================== */}
                    <div className="col-lg-8">

                        {/* GALERIE FOTO */}
                        <div style={cardStyles.container} className="mb-4">
                            <div
                                ref={galleryRef}
                                className="main-image-container position-relative"
                                style={{
                                    backgroundColor: '#000',
                                    touchAction: 'pan-y',
                                    overflow: 'hidden'
                                }}
                                onTouchStart={onGalleryTouchStart}
                                onTouchMove={onGalleryTouchMove}
                                onTouchEnd={onGalleryTouchEnd}
                            >
                                {imageLoading && (
                                    <div className="position-absolute top-50 start-50 translate-middle" style={{zIndex: 5}}>
                                        <div className="spinner-border" style={{color: primaryOrange}} role="status"></div>
                                    </div>
                                )}
                                {masina.tvaReductibil && <div className="tva-ribbon tva-ribbon--bottom">TVA Deductibil</div>}
                                {/* Track cu 3 slide-uri [prev, curent, next] care urmareste degetul */}
                                <div
                                    className="d-flex"
                                    style={{
                                        height: '100%',
                                        transform: trackTransform,
                                        transition: trackTransition,
                                        willChange: 'transform'
                                    }}
                                    onTransitionEnd={onTrackTransitionEnd}
                                >
                                    {slideIndices.map((idx, pos) => {
                                        const isCurrent = idx === currentImageIndex && (!multi || pos === 1);
                                        return (
                                            <div key={`${idx}-${pos}`} className="flex-shrink-0" style={{width: '100%', height: '100%'}}>
                                                <img
                                                    src={srcFor(idx)}
                                                    alt={`${masina.marca} ${masina.model}${multi ? ` - Fotografie ${idx + 1}` : ''}`}
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        cursor: 'zoom-in',
                                                        opacity: isCurrent && imageLoading ? 0 : 1,
                                                        transition: 'opacity 0.3s ease',
                                                        backgroundColor: '#000'
                                                    }}
                                                    onLoad={() => { loadedImagesRef.current.add(srcFor(idx)); if (isCurrent) setImageLoading(false); }}
                                                    onError={(e) => { e.currentTarget.src = BROKEN_IMG_PLACEHOLDER; if (isCurrent) setImageLoading(false); }}
                                                    onClick={isCurrent ? () => {
                                                        // un swipe nu trebuie sa deschida lightbox-ul
                                                        if (galleryDragRef.current.horizontal) return;
                                                        openLightbox(currentImageIndex);
                                                    } : undefined}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {galerieFoto.length > 1 && (
                                    <>
                                        <button
                                            className="position-absolute start-0 top-50 translate-middle-y ms-2 ms-md-3 btn rounded-circle d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                background: 'rgba(255,255,255,0.95)',
                                                border: 'none'
                                            }}
                                            onClick={() => slideTo('prev')}
                                        >
                                            <i className="bi bi-chevron-left" style={{color: trustNavy}}></i>
                                        </button>
                                        <button
                                            className="position-absolute end-0 top-50 translate-middle-y me-2 me-md-3 btn rounded-circle d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                background: 'rgba(255,255,255,0.95)',
                                                border: 'none'
                                            }}
                                            onClick={() => slideTo('next')}
                                        >
                                            <i className="bi bi-chevron-right" style={{color: trustNavy}}></i>
                                        </button>

                                        <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3">
                                            {/* Dot indicators for mobile */}
                                            <div
                                                className="d-flex align-items-center px-2 py-1"
                                                style={{
                                                    background: 'rgba(0,0,0,0.7)',
                                                    backdropFilter: 'blur(10px)',
                                                    borderRadius: '20px'
                                                }}
                                            >
                                                {galerieFoto.length <= 6 ? (
                                                    // Show dots if 6 or fewer images (zona de atingere ~28px, punctul ramane mic)
                                                    galerieFoto.map((_, idx) => (
                                                        <div
                                                            key={idx}
                                                            onClick={() => handleThumbnailClick(idx)}
                                                            style={{ padding: '10px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                            role="button"
                                                            aria-label={`Fotografia ${idx + 1}`}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: currentImageIndex === idx ? '20px' : '8px',
                                                                    height: '8px',
                                                                    borderRadius: '4px',
                                                                    background: currentImageIndex === idx ? primaryOrange : 'rgba(255,255,255,0.5)',
                                                                    transition: 'all 0.3s ease'
                                                                }}
                                                            />
                                                        </div>
                                                    ))
                                                ) : (
                                                    // Show counter if more than 6 images
                                                    <span style={{ color: 'white', fontSize: '0.8rem', fontWeight: '500' }}>
                                                        {currentImageIndex + 1} / {galerieFoto.length}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                <button
                                    className="position-absolute end-0 top-0 me-2 me-md-3 mt-2 mt-md-3 btn rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        background: 'rgba(255,255,255,0.95)',
                                        border: 'none'
                                    }}
                                    onClick={() => openLightbox(currentImageIndex)}
                                >
                                    <i className="bi bi-arrows-fullscreen" style={{color: trustNavy}}></i>
                                </button>

                                <button
                                    className="position-absolute start-0 top-0 ms-2 ms-md-3 mt-2 mt-md-3 btn rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        background: copied ? '#10b981' : 'rgba(255,255,255,0.95)',
                                        border: 'none',
                                        transition: 'background 0.2s ease'
                                    }}
                                    aria-label={copied ? 'Link copiat' : 'Distribuie anunțul'}
                                    onClick={handleShare}
                                >
                                    <i className={`bi ${copied ? 'bi-check-lg' : 'bi-share-fill'}`} style={{color: copied ? '#ffffff' : trustNavy, fontSize: '0.9rem'}}></i>
                                </button>
                            </div>

                            {/* Thumbnails */}
                            {galerieFoto.length > 1 && (
                                <div className="p-2 p-md-3" style={{background: '#f8f9fa'}}>
                                    <div ref={thumbStripRef} className="d-flex gap-2 overflow-auto pb-1" style={{scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch'}}>
                                        {galerieThumb.map((img, index) => (
                                            <div
                                                key={index}
                                                className="flex-shrink-0"
                                                style={{
                                                    width: isMobile ? '88px' : '70px',
                                                    height: isMobile ? '66px' : '52px',
                                                    cursor: 'pointer',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    border: currentImageIndex === index
                                                        ? `3px solid ${primaryOrange}`
                                                        : '3px solid transparent',
                                                    transition: 'all 0.3s ease',
                                                    opacity: currentImageIndex === index ? 1 : 0.6,
                                                    boxShadow: currentImageIndex === index
                                                        ? `0 4px 12px ${primaryOrange}30`
                                                        : 'none'
                                                }}
                                                onClick={() => handleThumbnailClick(index)}
                                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                                onMouseLeave={(e) => {
                                                    if (currentImageIndex !== index) e.currentTarget.style.opacity = 0.6;
                                                }}
                                            >
                                                <img
                                                    src={img}
                                                    alt={`${masina.marca} ${masina.model} - Fotografie ${index + 1}`}
                                                    className="w-100 h-100"
                                                    loading="lazy"
                                                    style={{objectFit: 'cover'}}
                                                    onError={(e) => { e.currentTarget.src = BROKEN_IMG_PLACEHOLDER; }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SIDEBAR MOBIL - APARE DOAR PE MOBIL (d-lg-none) */}
                        {/* SIDEBAR MOBIL - Apare doar pe mobil */}
                        <div className="d-lg-none mb-4">
                            <div style={sidebarStyles.container}>

                                {/* Header cu Preț */}
                                <div style={sidebarStyles.priceHeader}>
                                    <div style={sidebarStyles.priceHeaderPattern}></div>

                                    <p style={sidebarStyles.carTitle}>
                                        {masina.marca} {masina.model}
                                    </p>

                                    <div style={sidebarStyles.priceMain}>
                        <span style={sidebarStyles.priceEuro}>
                            {formatPret(masina.pret)} €
                        </span>
                                        {cursValutar && masina.pret && (
                                            <span style={sidebarStyles.priceRon}>
                                        ≈ {formatPret(Math.round(masina.pret * cursValutar))} RON
                                    </span>
                                        )}
                                    </div>
                                </div>

                                {/* Butoane Contact — telefon si WhatsApp impart un rand, raportul ramane lat */}
                                <div style={sidebarStyles.contactSection}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <a
                                            href="tel:0741811511"
                                            style={{...sidebarStyles.btnPhone, flex: 1, width: 'auto', marginBottom: 0, borderRadius: '50px', padding: '16px 12px', fontSize: '1rem'}}
                                        >
                                            <i className="bi bi-telephone-fill" style={{color: primaryOrange}}></i>
                                            Sună acum
                                        </a>
                                        <a
                                            href={`https://wa.me/40741811511?text=Salut, sunt interesat de ${masina.marca} ${masina.model}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{...sidebarStyles.btnWhatsapp, flex: 1, width: 'auto', borderRadius: '50px', padding: '16px 12px', fontSize: '1rem'}}
                                        >
                                            <i className="bi bi-whatsapp"></i>
                                            WhatsApp
                                        </a>
                                    </div>
                                    {masina.raportCarvertical && (
                                        <a
                                            href={`${API_URL}/parc-auto/${masina.id}/raport`}
                                            style={{...sidebarStyles.btnRaport, borderRadius: '50px', padding: '16px 24px'}}
                                        >
                                            Descarcă Raport
                                            {/* wordmark-ul e albastru; pe butonul albastru il facem alb */}
                                            <img src="/carvertical-wordmark.png" alt="CarVertical" style={{height: '15px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', marginTop: '1px'}} />
                                        </a>
                                    )}
                                </div>

                                {/* Servicii Incluse - mobile */}
                                <div style={{padding: '0 16px 20px'}}>
                                    <div style={{borderTop: '1px solid #f0f0f0', paddingTop: '16px'}}>
                                        <p style={{fontSize: '0.75rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px'}}>
                                            Servicii Incluse
                                        </p>
                                        {[
                                            {icon: 'bi-lightning-charge-fill', text: 'Finanțare rapidă (sub 2h)'},
                                            {icon: 'bi-arrow-left-right', text: 'Buy-Back pe loc'},
                                            {icon: 'bi-card-checklist', text: 'Numere provizorii'},
                                            {icon: 'bi-shield-check', text: 'Istoric verificat CarVertical'}
                                        ].map((item, idx, arr) => (
                                            <div key={idx} className="d-flex align-items-center gap-3" style={{
                                                padding: '11px 0',
                                                borderBottom: idx < arr.length - 1 ? '1px solid #f5f5f5' : 'none'
                                            }}>
                                                <div style={{width: '32px', height: '32px', borderRadius: '8px', background: `${primaryOrange}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                                                    <i className={`bi ${item.icon}`} style={{color: primaryOrange, fontSize: '0.95rem'}}></i>
                                                </div>
                                                <span style={{fontSize: '0.875rem', color: '#444', fontWeight: '500'}}>{item.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* ----------------------------------------------------------- */}

                            </div>
                        </div>

                        {/* SPECIFICAȚII TEHNICE */}
                        <div style={cardStyles.container}>
                            <div style={cardStyles.header}>
                                {!isMobile && <div style={cardStyles.headerPattern}></div>}
                                <h5 style={cardStyles.headerTitle}>
                                    <TbEngine size={isMobile ? 20 : 24} color={isMobile ? trustNavy : primaryOrange} />
                                    Specificații Tehnice
                                </h5>
                            </div>
                            <div style={cardStyles.body}>
                                <div className="row">
                                    <div className="col-md-6 pe-md-4">
                                        {specificatiiStanga.map((spec, index) => (
                                            <div
                                                key={index}
                                                // pe mobil coloanele se stivuiesc: ultimul rand din stanga
                                                // are nevoie de linie pentru ca tabelul continua dedesubt
                                                style={index === specificatiiStanga.length - 1 && !isMobile ? specStyles.rowLast : specStyles.row}
                                            >
                                                <span style={specStyles.label}>{spec.label}</span>
                                                <span style={spec.highlight ? specStyles.valueHighlight : specStyles.value}>
                                                    {spec.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="col-md-6 ps-md-4" style={{borderLeft: isMobile ? 'none' : '1px solid #f0f0f0'}}>
                                        {specificatiiDreapta.map((spec, index) => (
                                            <div
                                                key={index}
                                                style={index === specificatiiDreapta.length - 1 ? specStyles.rowLast : specStyles.row}
                                            >
                                                <span style={specStyles.label}>{spec.label}</span>
                                                <span style={spec.highlight ? specStyles.valueHighlight : specStyles.value}>
                                                    {spec.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* DESCRIERE */}
                        <div style={cardStyles.container}>
                            <div style={cardStyles.header}>
                                {!isMobile && <div style={cardStyles.headerPattern}></div>}
                                <h5 style={cardStyles.headerTitle}>
                                    <i className="bi bi-file-text" style={{color: isMobile ? trustNavy : primaryOrange, fontSize: isMobile ? '1.1rem' : '1.3rem'}}></i>
                                    Descriere
                                </h5>
                            </div>
                            <div style={cardStyles.body}>
                                <p style={{
                                    color: '#4a5568',
                                    fontSize: '1rem',
                                    lineHeight: '1.8',
                                    margin: 0,
                                    whiteSpace: 'pre-line'
                                }}>
                                    {masina.descriere || "Fără descriere disponibilă pentru acest vehicul."}
                                </p>
                            </div>
                        </div>

                        {/* DOTĂRI ȘI OPȚIUNI */}
                        {listaDotari.length > 0 && (
                            <div style={cardStyles.container}>
                                <div style={cardStyles.header}>
                                    {!isMobile && <div style={cardStyles.headerPattern}></div>}
                                    <h5 style={cardStyles.headerTitle}>
                                        <i className="bi bi-stars" style={{color: isMobile ? trustNavy : primaryOrange, fontSize: isMobile ? '1.1rem' : '1.3rem'}}></i>
                                        Dotări și Opțiuni
                                        <span
                                            style={{
                                                background: isMobile ? `${primaryOrange}15` : 'rgba(255,255,255,0.2)',
                                                color: isMobile ? primaryOrange : 'white',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                marginLeft: 'auto',
                                                fontWeight: '700'
                                            }}
                                        >
                                            {listaDotari.length} dotări
                                        </span>
                                    </h5>
                                </div>
                                <div style={{padding: '8px'}}>
                                    <div style={dotariStyles.grid}>
                                        {listaDotari.map((dotare, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    ...dotariStyles.item,
                                                    borderRight: (index + 1) % 2 === 0 ? 'none' : '1px solid #f5f5f5',
                                                    borderBottom: index >= listaDotari.length - 2 ? 'none' : '1px solid #f5f5f5'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <div style={dotariStyles.dot}></div>
                                                <span style={dotariStyles.text}>{dotare.trim()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ==================== COLOANA DREAPTA - SIDEBAR DESKTOP (STICKY) ==================== */}
                    {/* d-none d-lg-block = Apare DOAR pe desktop (lg+) */}
                    <div className="col-lg-4 d-none d-lg-block" style={{ position: 'sticky', top: '100px', zIndex: 10 }}>
                        <div style={sidebarStyles.container}>

                            {/* Header cu Preț */}
                            <div style={sidebarStyles.priceHeader}>
                                <div style={sidebarStyles.priceHeaderPattern}></div>

                                <p style={sidebarStyles.carTitle}>
                                    {masina.marca} {masina.model}
                                </p>

                                <div style={sidebarStyles.priceMain}>
                                    <span style={sidebarStyles.priceEuro}>
                                        {formatPret(masina.pret)} €
                                    </span>
                                    {cursValutar && masina.pret && (
                                        <span style={sidebarStyles.priceRon}>
                                            ≈ {formatPret(Math.round(masina.pret * cursValutar))} RON
                                        </span>
                                    )}
                                </div>

                            </div>

                            {/* Butoane Contact */}
                            <div style={sidebarStyles.contactSection}>
                                <a
                                    href="tel:0741811511"
                                    style={sidebarStyles.btnPhone}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <i className="bi bi-telephone-fill" style={{color: primaryOrange}}></i>
                                    +40 741 811 511
                                </a>
                                <a
                                    href={`https://wa.me/40741811511?text=Salut, sunt interesat de ${masina.marca} ${masina.model}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={sidebarStyles.btnWhatsapp}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <i className="bi bi-whatsapp"></i>
                                    WhatsApp
                                </a>
                                {masina.raportCarvertical && (
                                    <a
                                        href={`${API_URL}/parc-auto/${masina.id}/raport`}
                                        style={sidebarStyles.btnRaport}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        Descarcă Raport
                                        {/* wordmark-ul e albastru; pe butonul albastru il facem alb */}
                                        <img src="/carvertical-wordmark.png" alt="CarVertical" style={{height: '15px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', marginTop: '1px'}} />
                                    </a>
                                )}
                            </div>

                            {/* Servicii Premium */}
                            <div style={sidebarStyles.servicesSection}>
                                <div style={sidebarStyles.servicesHeader}>
                                    <div style={sidebarStyles.servicesLine}></div>
                                    <span style={sidebarStyles.servicesTitle}>Servicii Premium</span>
                                    <div style={{...sidebarStyles.servicesLine, background: 'linear-gradient(90deg, transparent 0%, #e0e0e0 100%)'}}></div>
                                </div>

                                <div style={sidebarStyles.serviceItem}>
                                    <div style={sidebarStyles.serviceIcon}>
                                        <i className="bi bi-lightning-charge-fill" style={{color: primaryOrange, fontSize: '1rem'}}></i>
                                    </div>
                                    <div>
                                        <h6 style={sidebarStyles.serviceTitle}>Finanțare Rapidă</h6>
                                        <p style={sidebarStyles.serviceDesc}>Aprobare în sub 2h cu rate fixe</p>
                                    </div>
                                </div>

                                <div style={sidebarStyles.serviceItem}>
                                    <div style={sidebarStyles.serviceIcon}>
                                        <i className="bi bi-arrow-left-right" style={{color: primaryOrange, fontSize: '1rem'}}></i>
                                    </div>
                                    <div>
                                        <h6 style={sidebarStyles.serviceTitle}>Sistem Buy-Back</h6>
                                        <p style={sidebarStyles.serviceDesc}>Evaluăm mașina ta pe loc</p>
                                    </div>
                                </div>

                                <div style={sidebarStyles.serviceItem}>
                                    <div style={sidebarStyles.serviceIcon}>
                                        <i className="bi bi-camera-video-fill" style={{color: primaryOrange, fontSize: '1rem'}}></i>
                                    </div>
                                    <div>
                                        <h6 style={sidebarStyles.serviceTitle}>Apel Video & Test Drive</h6>
                                        <p style={sidebarStyles.serviceDesc}>Vizualizare live prin WhatsApp</p>
                                    </div>
                                </div>

                                <div style={sidebarStyles.serviceItemLast}>
                                    <div style={sidebarStyles.serviceIcon}>
                                        <i className="bi bi-card-checklist" style={{color: primaryOrange, fontSize: '1rem'}}></i>
                                    </div>
                                    <div>
                                        <h6 style={sidebarStyles.serviceTitle}>Numere Provizorii</h6>
                                        <p style={sidebarStyles.serviceDesc}>Numere roșii și asigurare pe loc</p>
                                    </div>
                                </div>
                            </div>

                            {/* Trust Banner */}
                            <div style={sidebarStyles.trustBanner}>
                                <i className="bi bi-shield-fill-check" style={{color: primaryOrange, fontSize: '1.1rem'}}></i>
                                <span style={sidebarStyles.trustText}>Transparență 100% cu istoric verificat</span>
                            </div>

                            {/* Footer */}
                            <div style={sidebarStyles.footer}>
                                <p style={sidebarStyles.footerText}>
                                    *Soluții de finanțare pentru persoane fizice și juridice
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* LIGHTBOX MODAL */}
            {showLightbox && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        zIndex: 9999,
                        backdropFilter: 'blur(10px)',
                        // tragerea in jos estompeaza fundalul inainte de inchidere
                        opacity: 1 - Math.min(lbDragY / 400, 0.6),
                        transition: lbTouching ? 'none' : 'opacity 0.25s ease'
                    }}
                    onClick={closeLightbox}
                >
                    <button
                        className="position-absolute top-0 end-0 m-4 btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                        style={{width: '50px', height: '50px', zIndex: 10000}}
                        onClick={closeLightbox}
                    >
                        <i className="bi bi-x-lg fs-4"></i>
                    </button>

                    <div className="position-absolute top-0 start-50 translate-middle-x mt-4">
                        <span className="badge bg-white text-dark px-4 py-2" style={{fontSize: '1rem'}}>
                            {currentImageIndex + 1} / {galerieFoto.length}
                        </span>
                    </div>

                    {galerieFoto.length > 1 && (
                        <>
                            <button
                                className="position-absolute start-0 top-50 translate-middle-y ms-4 btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                                style={{width: '50px', height: '50px'}}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrevImage();
                                }}
                            >
                                <i className="bi bi-chevron-left fs-4"></i>
                            </button>
                            <button
                                className="position-absolute end-0 top-50 translate-middle-y me-4 btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                                style={{width: '50px', height: '50px'}}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNextImage();
                                }}
                            >
                                <i className="bi bi-chevron-right fs-4"></i>
                            </button>
                        </>
                    )}

                    <img
                        src={galerieFoto[currentImageIndex]}
                        alt={`${masina.marca} ${masina.model}`}
                        className="img-fluid"
                        style={{
                            maxHeight: '90vh',
                            maxWidth: '90vw',
                            objectFit: 'contain',
                            touchAction: 'none',
                            transform: `translate(${lbPan.x}px, ${lbPan.y + lbDragY}px) scale(${lbZoom})`,
                            transition: lbTouching ? 'none' : 'transform 0.25s ease',
                            cursor: lbZoom > 1 ? 'grab' : 'zoom-in',
                            willChange: 'transform'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={toggleLbZoom}
                        onError={(e) => { e.currentTarget.src = BROKEN_IMG_PLACEHOLDER; }}
                        onTouchStart={onLbTouchStart}
                        onTouchMove={onLbTouchMove}
                        onTouchEnd={onLbTouchEnd}
                    />
                </div>
            )}
        </div>
    );
}

export default MasinaDetalii;
